import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../context/UserContext';
import { useVoice } from '../context/VoiceContext';
import { getWordsForActivity } from '../services/contentService';
import { logActivity, checkAndAwardAchievements } from '../services/activityService';
import { voiceSimilarity, MAX_VOICE_ATTEMPTS, PASS_THRESHOLD, speechSupported } from '../utils/voiceUtils';
import './Activity.css';

const ScrambleWordsActivity = ({ onComplete }) => {
  const { currentUser } = useUser();
  const { microphoneGranted } = useVoice();
  const [words, setWords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [attempts, setAttempts] = useState(3);
  const [feedback, setFeedback] = useState('');
  const [userAnswer, setUserAnswer] = useState('');
  const [completed, setCompleted] = useState(false);
  const [wordDone, setWordDone] = useState(false);

  // Voice state
  const [isRecording, setIsRecording] = useState(false);
  const [voiceAttempts, setVoiceAttempts] = useState(0);
  const [voiceFeedback, setVoiceFeedback] = useState('');

  const autoAdvanceTimerRef = useRef(null);
  const watchdogTimerRef = useRef(null);
  const nextWordRef = useRef(null);

  useEffect(() => {
    if (!currentUser) return;
    setIsLoading(true);
    getWordsForActivity(currentUser.id, 'scramble').then(({ data }) => {
      const filtered = (data || []).filter(w => w.word.length === 3).slice(0, 25);
        setWords(filtered);
      setIsLoading(false);
    });
  }, [currentUser]);

  useEffect(() => {
    return () => {
      if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
      if (watchdogTimerRef.current) clearTimeout(watchdogTimerRef.current);
    };
  }, []);

  const checkAnswer = async () => {
    if (words.length === 0 || wordDone) return;
    const attemptsUsed = 3 - attempts + 1;
    if (userAnswer.toUpperCase() === words[currentIndex].word) {
      setFeedback('Correct! Great job!');
      setWordDone(true);
      await logActivity(currentUser.id, 'scramble', words[currentIndex].id, true, attemptsUsed);
      checkAndAwardAchievements(currentUser.id);
      autoAdvanceTimerRef.current = setTimeout(() => nextWordRef.current(), 1500);
    } else {
      const remaining = attempts - 1;
      setAttempts(remaining);
      if (remaining <= 0) {
        setFeedback(`The correct word was: ${words[currentIndex].word}`);
        setWordDone(true);
        await logActivity(currentUser.id, 'scramble', words[currentIndex].id, false, 3);
        autoAdvanceTimerRef.current = setTimeout(() => nextWordRef.current(), 2000);
      } else {
        setFeedback('Try again!');
      }
    }
  };

  const reset = () => {
    setUserAnswer('');
    setFeedback('');
  };

  const startVoiceRecording = () => {
    if (!speechSupported || !microphoneGranted) return;
    if (voiceAttempts >= MAX_VOICE_ATTEMPTS) {
      setVoiceFeedback('Max attempts reached. Try again later.');
      return;
    }

    setIsRecording(true);
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SR();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    let resultHandled = false;
    const clearWatchdog = () => {
      if (watchdogTimerRef.current) { clearTimeout(watchdogTimerRef.current); watchdogTimerRef.current = null; }
    };

    recognition.onresult = (event) => {
      resultHandled = true;
      clearWatchdog();
      const transcript = event.results[0][0].transcript.toUpperCase();
      const currentWord = words[currentIndex].word.toUpperCase();
      const score = voiceSimilarity(transcript, currentWord);

      setVoiceAttempts((prev) => prev + 1);

      if (score >= PASS_THRESHOLD) {
        setVoiceFeedback(`✓ Correct! "${currentWord}" (${score}%)`);
        setUserAnswer(words[currentIndex].word);
        setWordDone(true);
        logActivity(currentUser.id, 'scramble', words[currentIndex].id, true, (3 - attempts) + 1);
        checkAndAwardAchievements(currentUser.id);
        autoAdvanceTimerRef.current = setTimeout(() => nextWordRef.current(), 1500);
      } else {
        setVoiceFeedback(`✗ Try again. You said "${transcript}" (${score}%)`);
      }

      setIsRecording(false);
    };

    recognition.onerror = (event) => {
      resultHandled = true;
      clearWatchdog();
      if (event.error === 'aborted') { setIsRecording(false); return; }
      if (event.error === 'not-allowed' || event.error === 'audio-capture') {
        setVoiceFeedback('Microphone access denied. Please allow microphone access in your browser and try again.');
      } else if (event.error === 'no-speech') {
        setVoiceFeedback("I didn't hear anything — tap and say the word a bit louder.");
      } else if (event.error === 'network') {
        setVoiceFeedback('Voice check needs an internet connection right now. Please check your connection and try again.');
      } else {
        setVoiceFeedback("Couldn't hear you clearly. Please try again.");
      }
      setIsRecording(false);
    };

    // If recognition ends without ever producing a result or error, reset the
    // button so it doesn't stay stuck on "Listening...".
    recognition.onend = () => {
      clearWatchdog();
      if (!resultHandled) {
        setIsRecording(false);
        setVoiceFeedback("Didn't catch that — tap and try again.");
      }
    };

    try {
      recognition.start();
      // Watchdog: some browsers (notably desktop Chrome) can hang in the
      // listening state without ever firing onresult/onerror/onend. Force a
      // reset so the button never sticks on "Listening...".
      watchdogTimerRef.current = setTimeout(() => {
        if (resultHandled) return;
        resultHandled = true;
        try { recognition.abort(); } catch (e) { /* noop */ }
        setIsRecording(false);
        setVoiceFeedback("Voice check timed out — tap and try again.");
      }, 8000);
    } catch (err) {
      setIsRecording(false);
      setVoiceFeedback('Could not start the microphone. Please tap and try again.');
    }
  };

  const nextWord = () => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    if (currentIndex + 1 >= words.length) {
      setCompleted(true);
      return;
    }
    setCurrentIndex(currentIndex + 1);
    setAttempts(3);
    setUserAnswer('');
    setFeedback('');
    setWordDone(false);
    // Reset voice state
    setVoiceAttempts(0);
    setVoiceFeedback('');
  };
  nextWordRef.current = nextWord;

  if (isLoading) {
    return (
      <div className="activity-container">
        <p style={{ textAlign: 'center', padding: '2rem' }}>Loading words...</p>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="activity-container">
        <div className="activity-header scramble">
          <h2><i className="fas fa-star" aria-hidden="true"></i> All Done!</h2>
        </div>
        <div className="activity-content" style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
          <p style={{ fontSize: '1.2rem', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Fantastic job!
          </p>
          <p style={{ color: '#555', marginBottom: '2rem' }}>
            You unscrambled all {words.length} word{words.length !== 1 ? 's' : ''}!
          </p>
          <button className="btn btn-accent" onClick={onComplete}>
            <i className="fas fa-chart-bar" aria-hidden="true"></i> See My Progress
          </button>
        </div>
      </div>
    );
  }

  if (words.length === 0) {
    return (
      <div className="activity-container">
        <div className="activity-header scramble">
          <h2><i className="fas fa-puzzle-piece"></i> Scramble Words</h2>
        </div>
        <div className="activity-content">
          <p style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
            No scramble words assigned yet. Ask your teacher to assign words!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="activity-container">
      <div className="activity-header scramble">
        <h2><i className="fas fa-puzzle-piece"></i> Scramble Words</h2>
        <p>Rearrange the letters to form the correct word</p>
      </div>

      <div className="activity-content">
        <div className="word-display">
          {words[currentIndex].scrambled}
        </div>

        <div className="input-group">
          <input
            type="text"
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && checkAnswer()}
            placeholder="Enter your answer"
            className="form-control"
            aria-label="Enter your unscrambled answer"
            maxLength={50}
            disabled={wordDone}
          />
          <button className="btn btn-primary" onClick={checkAnswer} disabled={wordDone}>
            Check Answer
          </button>
          {speechSupported && microphoneGranted && (
            <button
              className={`btn btn-secondary ${isRecording ? 'recording' : ''}`}
              onClick={startVoiceRecording}
              disabled={wordDone || voiceAttempts >= MAX_VOICE_ATTEMPTS || isRecording}
              aria-label="Record voice input"
            >
              <i className={isRecording ? 'fas fa-stop' : 'fas fa-microphone'} aria-hidden="true"></i>
              {isRecording ? 'Listening…' : '🎤'}
            </button>
          )}
        </div>

        <div className="controls">
          <button className="btn btn-secondary" onClick={reset}>
            <i className="fas fa-redo"></i> Reset
          </button>
          <button className="btn btn-accent" onClick={nextWord}>
            <i className="fas fa-arrow-right"></i> Next Word
          </button>
        </div>

        <div
          className={`feedback ${feedback.includes('Correct') ? 'success' : 'error'}`}
          role="status"
          aria-live="polite"
        >{feedback}</div>

        {voiceFeedback && (
          <div className="voice-feedback" role="status" aria-live="polite">
            {voiceFeedback}
          </div>
        )}

        {speechSupported && microphoneGranted && voiceAttempts > 0 && (
          <div className="voice-attempts">
            Voice attempts: {voiceAttempts}/{MAX_VOICE_ATTEMPTS}
          </div>
        )}

        <div className="attempts" aria-live="polite">
          Word {currentIndex + 1} of {words.length} &nbsp;|&nbsp; Attempts remaining: {attempts}
        </div>
      </div>
    </div>
  );
};

export default ScrambleWordsActivity;
